import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { CaptureOff } from '../components/CaptureOff';
import { useCaptureModes } from '../lib/captureModes';

/**
 * ═══ CARD-SCAN ATTENDANCE ═══
 *
 * The teacher's phone reads the QR on each ID card at the door.
 *
 * ⚠ SCANNING MARKS PRESENT. IT NEVER MARKS ABSENT. Absence is decided once, deliberately, when the
 * session is ended — because a scan that inferred absence as it went would mark the whole class
 * absent in the first second, and any interruption (a bell, a flat battery, a teacher called away)
 * would leave a register asserting that thirty children were not at school.
 *
 * ⚠ MATCHING HAPPENS IN THE BROWSER, against the roll loaded once. A round trip per scan is 300ms
 * of a child standing at a door, forty-five times. The server is only asked when a code does NOT
 * match this class — which is rare, and is exactly when the extra information is worth waiting for.
 *
 * ⚠ AND THERE IS ALWAYS A KEYBOARD. Cameras fail, cards get bent, and some Android browsers have no
 * barcode support at all. A scanner with no manual path is a scanner that strands a teacher.
 */

interface Row {
  studentId: string; admissionNumber: string; fullName: string;
  classLabel: string | null; status: string; hasPhoto: boolean;
}
interface Roster {
  institutionId: string; date: string; classLabel: string | null;
  alreadyMarked: boolean; students: Row[];
}
interface Group { id: string; label: string; people: number }
interface StudentHit { id: string; fullName: string; admissionNumber: string; classLabel: string | null }

type Outcome = 'Marked' | 'Duplicate' | 'WrongClass' | 'Unknown' | 'Unreadable';
interface Feedback { outcome: Outcome; message: string; at: number }

const today = () => new Date().toISOString().slice(0, 10);

/**
 * ⚠ MIRRORS ScanSession.Normalise ON THE SERVER, and deliberately just as strict. A URL is refused
 * rather than having its last segment taken: guessing would turn a competitor's QR — or a poster on
 * the classroom wall — into one of our students.
 */
function normalise(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();
  if (s.includes('://') || /[\r\n]/.test(s)) return null;
  if (s.length < 2 || s.length > 60) return null;
  const up = s.toUpperCase();
  return /^[A-Z0-9\-_/]+$/.test(up) ? up : null;
}

export function ScanAttendancePage() {
  const { allows: captureAllows, resolved: captureResolved } = useCaptureModes();
  const [institutionId, setInstitutionId] = useState('');
  const [classes, setClasses] = useState<Group[]>([]);
  const [classLabel, setClassLabel] = useState('');
  const [date, setDate] = useState(today());

  const [roster, setRoster] = useState<Roster | null>(null);
  const [scanned, setScanned] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [typed, setTyped] = useState('');
  const [camera, setCamera] = useState<'off' | 'on' | 'unsupported'>('off');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const video = useRef<HTMLVideoElement | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const stopped = useRef(false);

  useEffect(() => {
    api.get<Array<{ id: string }>>('/api/institutions')
      .then((xs) => setInstitutionId((i) => i || xs[0]?.id || '')).catch(() => undefined);
    api.get<Group[]>('/api/exports/register/groups?scope=students')
      .then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, []);

  const load = useCallback(async () => {
    if (!institutionId || !classLabel) { setRoster(null); return; }
    setBusy(true); setError(null); setSaved(false);
    try {
      const q = new URLSearchParams({ institutionId, date, classLabel });
      const r = await api.get<Roster>(`/api/attendance/roster?${q}`);
      setRoster(r);
      // ⚠ A session STARTS EMPTY even when the register was taken earlier. The teacher is scanning
      // the room in front of them now; carrying yesterday's — or this morning's — ticks in would
      // mean a child who left at lunch stays marked present because nobody rescanned them.
      setScanned(new Set());
      setFeedback(null);
    } catch (e) {
      setRoster(null);
      setError(e instanceof ApiError ? e.message : 'Could not load the class.');
    } finally { setBusy(false); }
  }, [institutionId, classLabel, date]);

  useEffect(() => { void load(); }, [load]);

  const byAdmission = useMemo(() => {
    const m = new Map<string, Row>();
    for (const s of roster?.students ?? []) {
      const k = normalise(s.admissionNumber);
      if (k) m.set(k, s);
    }
    return m;
  }, [roster]);

  const say = (outcome: Outcome, message: string) => setFeedback({ outcome, message, at: Date.now() });

  const handle = useCallback(async (raw: string) => {
    const code = normalise(raw);
    if (!code) { say('Unreadable', 'That is not a student card.'); return; }

    const hit = byAdmission.get(code);
    if (hit) {
      if (scanned.has(hit.studentId)) { say('Duplicate', `${hit.fullName} is already marked.`); return; }
      setScanned((s) => new Set(s).add(hit.studentId));
      setSaved(false);
      say('Marked', `${hit.fullName} — present.`);
      return;
    }

    // ⚠ Only now does the server get asked, and only to NAME the card. "Meera is in X B, not this
    // one" is acted on in two seconds; "unknown card" sends a teacher to the office.
    try {
      const page = await api.get<{ items: StudentHit[] }>(
        `/api/students?take=5&search=${encodeURIComponent(code)}`);
      const other = page.items.find((x) => normalise(x.admissionNumber) === code);
      if (other) {
        say('WrongClass', `${other.fullName} is in ${other.classLabel ?? 'another class'}, not this one.`);
        return;
      }
    } catch { /* fall through to unknown — a lookup failure must not stop the queue */ }

    say('Unknown', `No student has admission number ${code}.`);
  }, [byAdmission, scanned]);

  // ── the camera ──
  const startCamera = async () => {
    const Detector = (window as unknown as { BarcodeDetector?: new (o?: unknown) => {
      detect(src: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
    } }).BarcodeDetector;

    if (!Detector || !navigator.mediaDevices?.getUserMedia) { setCamera('unsupported'); return; }

    try {
      // ⚠ The BACK camera. facingMode 'environment' — a scanner that opens the selfie camera is a
      // scanner nobody uses twice.
      stream.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      if (video.current) { video.current.srcObject = stream.current; await video.current.play(); }
      setCamera('on');
      stopped.current = false;

      const detector = new Detector({ formats: ['qr_code'] });
      let last = '';
      let lastAt = 0;

      const tick = async () => {
        if (stopped.current || !video.current) return;
        try {
          const codes = await detector.detect(video.current);
          const value = codes[0]?.rawValue;
          // ⚠ THE SAME CODE IS SEEN THIRTY TIMES A SECOND while a card sits in frame. Without this
          // guard the feedback line flickers and a duplicate warning fires continuously.
          if (value && (value !== last || Date.now() - lastAt > 2500)) {
            last = value; lastAt = Date.now();
            await handle(value);
          }
        } catch { /* a dropped frame is not an error */ }
        requestAnimationFrame(() => void tick());
      };
      void tick();
    } catch {
      setCamera('unsupported');
    }
  };

  const stopCamera = useCallback(() => {
    stopped.current = true;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    setCamera('off');
  }, []);

  // ⚠ Released when the screen goes away. A camera left running is a light on the back of a phone
  // in somebody's pocket and a battery gone by lunchtime.
  useEffect(() => stopCamera, [stopCamera]);

  const remaining = useMemo(
    () => (roster?.students ?? []).filter((s) => !scanned.has(s.studentId))
      .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [roster, scanned]);

  const finish = async () => {
    if (!roster) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/attendance/mark', {
        institutionId, date, classLabel,
        entries: roster.students.map((s) => ({
          studentId: s.studentId,
          status: scanned.has(s.studentId) ? 'Present' : 'Absent',
          remark: null,
        })),
      });
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the register.');
    } finally { setBusy(false); }
  };

  const tone = (o: Outcome) =>
    o === 'Marked' ? 'var(--ok, #067647)'
      : o === 'Duplicate' ? 'var(--ink-2)'
      : o === 'WrongClass' ? 'var(--warn)' : 'var(--danger)';

  // ⚠ AFTER every hook, never before one. An early return above a hook changes the hook order
  // between renders, which React fails on rather than warns about.
  if (captureResolved && !captureAllows('CardScan'))
    return <CaptureOff mode="Card scan" blurb="No institution in this school has it switched on. It needs printed ID cards to be worth anything." />;

  return (
    <div style={{ paddingBottom: 96 }}>
      <div style={{ marginBottom: 14 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Attendance</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Card scan</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '64ch' }}>
          Scan each student's ID card. Scanning marks them present — nobody is marked absent until
          you finish.
        </p>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 10,
                                     flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 170 }}>
          <label className="label">Class</label>
          <select className="input" value={classLabel} style={{ width: '100%' }}
            onChange={(e) => setClassLabel(e.target.value)}>
            <option value="">Choose a class</option>
            {classes.map((c) => <option key={c.id} value={c.label}>{c.label} · {c.people}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Date</label>
          <input className="input" type="date" value={date} max={today()}
            onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 12, color: 'var(--danger)', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {roster?.alreadyMarked && !saved && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13.5,
                                       borderLeft: '3px solid var(--warn)' }}>
          This register was already taken for {date}. Finishing replaces it.
        </div>
      )}

      {roster && (
        <>
          {/* ── the viewfinder ── */}
          <div className="card" style={{ padding: 14, marginBottom: 12 }}>
            {camera === 'on' ? (
              <>
                <video ref={video} playsInline muted
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover',
                           borderRadius: 10, background: '#000' }} />
                <button className="btn" style={{ marginTop: 10 }} onClick={stopCamera}>Stop the camera</button>
              </>
            ) : (
              <>
                <button className="btn btn-accent" onClick={() => void startCamera()}>Start the camera</button>
                {camera === 'unsupported' && (
                  // ⚠ Named, and with the way out beside it. "Camera error" leaves a teacher stuck
                  // in front of a class; this tells them the register can still be taken.
                  <p style={{ fontSize: 12.5, color: 'var(--warn)', margin: '10px 0 0', maxWidth: '58ch' }}>
                    This browser cannot read QR codes, or the camera was refused. Type the admission
                    numbers below instead, or use the photo roll.
                  </p>
                )}
              </>
            )}

            {/* ⚠ ALWAYS PRESENT, not a fallback that appears when something breaks. A bent card or a
                child who left theirs at home is a normal Tuesday, not an error state. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input className="input" value={typed} placeholder="Or type an admission number"
                style={{ flex: 1 }}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !typed.trim()) return;
                  void handle(typed);
                  setTyped('');
                }} />
              <button className="btn" disabled={!typed.trim()}
                onClick={() => { void handle(typed); setTyped(''); }}>Mark</button>
            </div>
          </div>

          {/* ⚠ ONE LINE, LARGE, AND IT IS THE WHOLE FEEDBACK. A teacher scanning a queue is looking
              at the children, not the screen — they need a colour and a name in peripheral vision. */}
          {feedback && (
            <div className="card" key={feedback.at}
              style={{ padding: '14px 16px', marginBottom: 12, fontSize: 16, fontWeight: 600,
                       color: tone(feedback.outcome),
                       borderLeft: `4px solid ${tone(feedback.outcome)}` }}>
              {feedback.message}
            </div>
          )}

          {/* ── who is still missing ── */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 650, marginBottom: 8 }}>
              Not scanned yet · {remaining.length}
            </div>
            {remaining.length === 0 ? (
              <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0 }}>Everyone has been scanned.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {/* ⚠ Tappable. At the end the teacher reads this list to the room, and the two
                    children whose cards failed get marked with a tap rather than a second queue. */}
                {remaining.map((s) => (
                  <button key={s.studentId} className="btn"
                    style={{ padding: '5px 10px', fontSize: 12.5 }}
                    onClick={() => { setScanned((x) => new Set(x).add(s.studentId)); setSaved(false); }}>
                    {s.fullName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {roster && roster.students.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          background: 'var(--surface)', borderTop: '1px solid var(--line)',
          padding: '10px 16px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 13.5 }}>
            <strong>{roster.students.length - remaining.length}</strong> scanned
            <span style={{ color: 'var(--ink-3)' }}> of {roster.students.length}</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            {saved && <span style={{ fontSize: 13, color: 'var(--ok, #067647)' }}>Saved.</span>}
            {/* ⚠ THE BUTTON STATES THE CONSEQUENCE, WITH THE NUMBER. "Finish" gets pressed without
                reading; "Finish — mark the other 7 absent" gets checked first. */}
            <button className="btn btn-accent" disabled={busy} onClick={() => void finish()}
              style={{ padding: '9px 18px' }}>
              {busy ? 'Saving…'
                : remaining.length === 0 ? 'Everyone is marked — save the register'
                : `Finish — mark the other ${remaining.length} absent`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
